/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { Molecule } from '../models/Molecule';
import { useStore } from '../stores/common';
import { useRefStore } from '../stores/commonRef';

// Clones of lab (testMolecules) entries carrying the LIVE frame, for undo
// snapshots taken before a removal. The store molecules hold the positions and
// velocities of the last save or placement — a run moves the atoms only in the
// engine and in the viewer's live copies (moleculesRef), and Save levels the
// store with them. A bare Molecule.clone(store[i]) therefore restored a deleted
// molecule at its pre-run spot, at rest, while the rest of the lab (carried by
// store-object identity through the rebuild) stayed where the run had taken it.
// Copies the same per-atom fields Save does (cloudProjectUtil.syncMoleculesFromRef),
// without that module's Firebase imports — the callers sit in the eager lab bundle.
// FULL clones: these put the SAME molecule back where it was, so its restraint,
// damping and trajectory flag come with it — and its Reset target. The engine
// atoms (shared with the live molecules) are the only place the Reset target
// lives; a store molecule has none. When the clone goes back into the store as
// a NEW object (undo of a delete / cut / clear / kept-state load), the viewer's
// rebuild has no live atom to carry the target from and would pin wherever the
// clone sits — the mid-run frame — as t = 0, so Reset no longer returned that
// molecule to the saved start. The rebuild reads the clone's own target instead.
// (A default clone is a NEW molecule — Copy / Paste — and deliberately starts
// without those per-atom settings, the Reset target included.)
const copyLiveAtoms = (clone: Molecule, live: Molecule | undefined, full: boolean): Molecule => {
  if (!live || live.atoms.length !== clone.atoms.length) return clone;
  for (const [j, a] of clone.atoms.entries()) {
    const b = live.atoms[j];
    if (full) {
      if (b.initialPosition) a.initialPosition = b.initialPosition.clone();
      if (b.initialVelocity) a.initialVelocity = b.initialVelocity.clone();
      if (b.initialOrientation) a.initialOrientation = b.initialOrientation.clone();
    }
    a.position.set(b.position.x, b.position.y, b.position.z);
    a.velocity.set(b.velocity.x, b.velocity.y, b.velocity.z);
    // A rod's axis and spin are part of its live frame too.
    if (b.orientation) a.orientation = b.orientation.clone();
    if (b.angularVelocity) a.angularVelocity = b.angularVelocity.clone();
    a.sigma = b.sigma;
    a.epsilon = b.epsilon;
    a.mass = b.mass;
    a.charge = b.charge;
    if (b.chargeSet) a.chargeSet = true;
    a.fixed = b.fixed;
    if (a.restraint && b.restraint) a.restraint.position.copy(b.restraint.position);
  }
  return clone;
};

const liveList = (storeLength: number): Molecule[] | null => {
  const live = useRefStore.getState().moleculesRef?.current;
  return live && live.length === storeLength ? live : null;
};

/**
 * Clone of the lab molecule at `index` with the live positions / velocities /
 * parameters. `full` (default) is the SAME molecule coming back (undo snapshots):
 * restraint, damping and trajectory flag included. `full = false` is a NEW
 * molecule (Copy): the shape the user sees, without those per-atom settings.
 */
export const cloneLabMoleculeLive = (index: number, full = true): Molecule | null => {
  const store = useStore.getState().projectState.testMolecules ?? [];
  const m = store[index];
  if (!m) return null;
  return copyLiveAtoms(Molecule.clone(m, full), liveList(store.length)?.[index], full);
};

/** Clones of every lab molecule with the live frame, in store order. */
export const cloneLabMoleculesLive = (): Molecule[] => {
  const store = useStore.getState().projectState.testMolecules ?? [];
  const live = liveList(store.length);
  return store.map((m, i) => copyLiveAtoms(Molecule.clone(m, true), live?.[i], true));
};
